<template>
  <div class="tw:w-full" :style="containerStyle">
    <div class="tw:flex tw:items-center tw:gap-12 tw:w-full">
      <div class="tw:flex-1 slider-container">
        <a-slider
          v-model:value="sliderValue"
          :class="sliderClass"
          :style="sliderStyle"
          v-bind="mergedAttrs"
          @change="handleChange"
          @afterChange="handleAfterChange"
        />
      </div>
      <InputNumberBaseAntd
        v-if="props.showValue"
        v-model="inputValue"
        :min="props.min"
        :max="props.max"
        borderless
        :controls="false"
        size="small"
        :step="props.step ?? undefined"
        :disabled="props.disabled"
        align="center"
        class="tw:w-[58px]"
        @change="handleInputChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, normalizeClass, useAttrs, type HTMLAttributes } from 'vue'
import InputNumberBaseAntd from './InputNumberBaseAntd.vue'

interface Props {
  modelValue?: number | number[]
  min?: number
  max?: number
  step?: number | null
  disabled?: boolean
  range?: boolean
  vertical?: boolean
  reverse?: boolean
  tooltip?: boolean | object | null
  marks?: Record<number, string | { style?: object; label?: string }>
  dots?: boolean
  included?: boolean
  width?: string
  height?: string
  borderless?: boolean
  class?: HTMLAttributes['class']
  showValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  min: 0,
  max: 100,
  step: 1,
  disabled: false,
  range: false,
  vertical: false,
  reverse: false,
  tooltip: false,
  marks: undefined,
  dots: false,
  included: true,
  width: undefined,
  height: undefined,
  borderless: false,
  class: '',
  showValue: false,
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: number | number[]]
  change: [value: number | number[]]
  afterChange: [value: number | number[]]
}>()

const attrs = useAttrs()

const sliderValue = computed({
  get: () => props.modelValue,
  set: (val: number | number[]) => {
    emit('update:modelValue', val)
  },
})

// Input value for showValue prop
const inputValue = computed({
  get: () => {
    const value = props.modelValue
    return Array.isArray(value) ? value[0] : value
  },
  set: (val: number) => {
    if (props.range) {
      const currentValue = Array.isArray(props.modelValue)
        ? props.modelValue
        : [props.modelValue, props.max]
      emit('update:modelValue', [val, currentValue[1]])
    } else {
      emit('update:modelValue', val)
    }
  },
})

const sliderClass = computed(() => {
  return normalizeClass(['slider-custom', { borderless: props.borderless }, props.class])
})

const containerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.width = props.width
  }
  if (props.height) {
    style.height = props.height
  }
  return style
})

const sliderStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width && !props.vertical) {
    style.width = props.width
  }
  if (props.height && props.vertical) {
    style.height = props.height
  }
  return style
})

// Merge props với attrs, props có priority cao hơn
const mergedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...restAttrs } = attrs
  return {
    ...restAttrs,
    min: props.min,
    max: props.max,
    step: props.step,
    disabled: props.disabled,
    range: props.range,
    vertical: props.vertical,
    reverse: props.reverse,
    tooltipOpen: props.tooltip,
    marks: props.marks,
    dots: props.dots,
    included: props.included,
  }
})

const handleChange = (value: number | number[]) => {
  emit('change', value)
}

const handleAfterChange = (value: number | number[]) => {
  emit('afterChange', value)
}

const handleInputChange = (value: number) => {
  if (props.range) {
    const currentValue = Array.isArray(props.modelValue)
      ? props.modelValue
      : [props.modelValue, props.max]
    emit('update:modelValue', [value, currentValue[1]])
    emit('change', [value, currentValue[1]])
  } else {
    emit('update:modelValue', value)
    emit('change', value)
  }
}
</script>

<style scoped lang="less">
.slider-container {
  overflow: hidden; // Clip handles that exceed boundaries
  padding: 0 16px; // Add padding to prevent handle overflow
  margin: 0 -16px; // Compensate padding to maintain width
}

:deep(.slider-custom) {
  width: 100%;
  font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
  font-size: var(--sds-typography-scale-02, 14px) !important;
  color: var(--sds-color-text-default-primary, #ffffff) !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
  // Rail (track background)
  .ant-slider-rail {
    background: var(--sds-color-border-default-secondary, rgba(255, 255, 255, 0.2)) !important;
    height: 4px !important;
  }

  // Track (filled portion)
  .ant-slider-track {
    background: var(--sds-color-border-brand-default, #d8dae2) !important;
    height: 4px !important;
  }

  // Handle (thumb)
  .ant-slider-handle {
    width: 16px !important;
    height: 16px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    transform-origin: center center !important;
    margin-left: -8px !important; // Center horizontally: -width / 2
    background: #ffffff !important;
    border: none !important;
    border-radius: 50% !important;
    box-shadow: none !important;
    outline: none !important;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease !important;
    
    // When left is 0%, align to left edge
    &[style*='left: 0%'],
    &[style*='left:0%'] {
      left: 0 !important;
      margin-left: 0 !important;
    }

    &[style*='left: 100%'],
    &[style*='left:100%'] {
      left: calc(100% - 8px) !important;
      margin-left: -8px !important;
    }


    &:hover,
    &:focus {
      transform: translateY(-50%) scale(1.1) !important;
      transform-origin: center center !important;
      box-shadow: 0 0 8px rgba(255, 255, 255, 0.6) !important;
    }

    &.ant-slider-handle-dragging {
      transform: translateY(-50%) scale(1.1) !important;
      transform-origin: center center !important;
      box-shadow: 0 0 8px rgba(255, 255, 255, 0.6) !important;
    }

    &::after {
      display: none !important;
    }

    &::before {
      display: none !important;
    }
  }

  // Marks
  .ant-slider-mark {
    font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
    font-size: var(--sds-typography-scale-01, 12px) !important;
    color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
    top: -6px !important;

    .ant-slider-mark-text {
      color: var(--sds-color-text-default-tertiary, #a6a6a6) !important;
    }
  }

  // Tooltip
  .ant-slider-tooltip {
    .ant-tooltip-inner {
      background: rgba(26, 26, 26, 0.95) !important;
      color: var(--sds-color-text-default-primary, #ffffff) !important;
      font-family: var(--sds-typography-family-poppins, 'Poppins', sans-serif) !important;
      font-size: var(--sds-typography-scale-02, 14px) !important;
      border: 1px solid var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
    }

    .ant-tooltip-arrow::before {
      background: rgba(26, 26, 26, 0.95) !important;
      border: 1px solid var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
    }
  }

  // Disabled state
  &.ant-slider-disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .ant-slider-rail {
      background: var(
        --sds-color-background-disabled-default,
        rgba(255, 255, 255, 0.02)
      ) !important;
    }

    .ant-slider-track {
      background: var(
        --sds-color-background-disabled-default,
        rgba(255, 255, 255, 0.02)
      ) !important;
    }

    .ant-slider-handle {
      background: var(
        --sds-color-background-disabled-default,
        rgba(255, 255, 255, 0.02)
      ) !important;
      border-color: var(--sds-color-border-default-primary, rgba(255, 255, 255, 0.2)) !important;
      cursor: not-allowed !important;
    }
  }

  // Vertical slider
  &.ant-slider-vertical {
    height: 100%;

    .ant-slider-rail {
      width: 4px !important;
      height: 100% !important;
    }

    .ant-slider-track {
      width: 4px !important;
    }

    .ant-slider-handle {
      margin-left: -6px !important;
      margin-top: 0 !important;
    }
  }
}
</style>
